import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogOverlay,
  Button,
  Flex,
  useDisclosure,
} from '@chakra-ui/react';
import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  FundingRequestStatusEnumGQLType,
  GetFundingRequestDocumentGQLType,
  GetOrCreateFundingRequestTemplateDocumentGQLType,
  useCancelFundingRequestMutation,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { Typography } from '~/packages/common-frontend/components/Typography';

export const CancelFundingRequestButton = ({
  fundingRequestId,
  creditFacilityId,
  fundingRequestStatus,
  canCancelOnPending = false,
  isDisabled,
}: {
  fundingRequestId: string;
  creditFacilityId: string;
  fundingRequestStatus: FundingRequestStatusEnumGQLType;
  canCancelOnPending?: boolean;
  isDisabled?: boolean;
}) => {
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef<HTMLButtonElement>(null);

  const [
    cancelFundingRequest,
    { loading: isLoading },
  ] = useCancelFundingRequestMutation({
    onCompleted: () => {
      navigate('/funding-requests');
    },
  });

  const handleCancelFundingRequest = useCallback(async () => {
    await cancelFundingRequest({
      variables: { fundingRequestId },
      refetchQueries: [
        {
          query: GetOrCreateFundingRequestTemplateDocumentGQLType,
          variables: {
            mode: 'get',
            fundingRequestId,
            creditFacilityId,
          },
        },
        {
          query: GetFundingRequestDocumentGQLType,
          variables: {
            fundingRequestId,
          },
        },
      ],
    });

    onClose();
  }, [cancelFundingRequest, creditFacilityId, fundingRequestId, onClose]);

  return fundingRequestStatus === 'submitted' ||
    (canCancelOnPending && fundingRequestStatus === 'pending') ? (
    <>
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Cancel Funding Request
            </AlertDialogHeader>

            <AlertDialogBody>
              <Typography variant="base">
                Are you sure you want to cancel the current funding request?
              </Typography>
            </AlertDialogBody>

            <AlertDialogFooter sx={{ gap: '1em' }}>
              <Button
                variant="secondary-muted"
                colorScheme="v2-aurora"
                ref={cancelRef}
                onClick={onClose}
              >
                Go Back
              </Button>
              <Button
                variant="primary"
                colorScheme="v2-aurora"
                onClick={handleCancelFundingRequest}
                isLoading={isLoading}
              >
                Confirm
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      <Flex w="100%" gap="1rem" justify="flex-end">
        <Button
          variant="secondary"
          colorScheme="v2-aurora"
          isLoading={isLoading}
          isDisabled={isDisabled}
          onClick={() => onOpen()}
        >
          Cancel Request
        </Button>
      </Flex>
    </>
  ) : null;
};
